import moment from 'moment';
import { useLocation } from 'react-router-dom';

import { timeLabel } from './eventTimes';
import { rangeHasSunday, venueOpensSundays } from './sundays';

// One published address, everywhere. Changed from yeddingllc@gmail.com on
// 2026-09-21: BRAND-CONTEXT.md section 8 records support@yedding.com as the
// support address, and two different addresses on one site reads as two
// different companies to a person and to an answer engine. The same swap
// was made in src/content/legal/terms.js. This constant feeds the footer
// Contact Us link on every page.
export const companyEmail = 'support@yedding.com';
export const companyPhone = '+18019234914';
// AEO-9: the four legal documents are HTML pages now, and the page is the
// only maintained copy. The PDFs stay in public/ at their old URLs, as a
// frozen archive, so an older link still resolves — but nothing links to
// them any more. Their text lives in src/content/legal.
// `deleteAccount` has no HTML page, so it stays a PDF.
export const faqPage = '/faq';
export const aboutPage = '/about';
export const howItWorksPage = '/how-it-works';
export const privacyPolicy = '/privacy';
export const deleteAccount = '/facebook-data-deletion.pdf';
export const termsOfUse = '/terms';
export const hostAgreement = '/host-agreement';
export const guestAgreement = '/guest-agreement';

const ANY_LOCATION = 'all';
export const isAnyLocation = (location) => !location || location === ANY_LOCATION;

// Venue's have an array of reserved dates. The dates are in reverse order
// because `dateRange` will always be in the future, and we want to exit
// the loop once the reserved dates are before the dateRange.
const _getIsAvailable = ({ start, end, badDates }) => {
  if (Array.isArray(badDates) && badDates.length) {
    for (let i = 0; i < badDates.length; i++) {
      const date = badDates[i];
      // if the unavailable date is before the start date-range, exit the loop early
      if (start.isAfter(date)) {
        break;
      }
      // if date is inside the range, return false
      if (end.isSameOrAfter(date)) {
        return false;
      }
      // else, unavailable dates are still in the future, outside our `dateRange`
    }
  }
  return true;
};
// Billing B1.5: a range with a Sunday needs a venue open on Sundays.
export const isSundayBlocked = (dateRange, venue, allSundaysOpen = false) => {
  const { startDate, endDate } = dateRange || {};
  return !venueOpensSundays(venue, allSundaysOpen) && rangeHasSunday(startDate, endDate);
};

export const isAvailable = (dateRange, venue, allSundaysOpen = false) => {
  const { startDate, endDate } = dateRange || {};
  const { bookedDates, excludedDates, disabled } = venue || {};

  const start = moment(startDate),
    end = moment(endDate);

  return (
    !disabled &&
    !isSundayBlocked(dateRange, venue, allSundaysOpen) &&
    _getIsAvailable({ start, end, badDates: bookedDates }) && // check booked dates
    _getIsAvailable({ start, end, badDates: excludedDates }) // check blacklisted dates
  );
};

export const countDays = ({ startDate, endDate }) => {
  const start = moment(startDate);
  const end = moment(endDate);
  const days = end.diff(start, 'days') + 1;
  return days;
};

export const getBookingDetails = (venue) => {
  const isBooking = !!venue.booking;
  const location = !venue.address
    ? ''
    : isBooking
    ? `${venue.address.street.trim()}, ${venue.address.city.trim()} ${venue.address.state.trim()} ${venue.address.zip.trim()}`
    : `${venue.address.city} ${venue.address.state}`;
  const startLabel = isBooking ? moment(venue.booking.start).format('MMM D') : '';
  const endLabel = isBooking ? moment(venue.booking.end).format('MMM D, YYYY') : '';
  const dateLabel = isBooking ? (venue.booking.start === venue.booking.end ? endLabel : `${startLabel} - ${endLabel}`) : '';
  const daysCount = isBooking ? countDays({ startDate: venue.booking.start, endDate: venue.booking.end }) : '';
  // const bookingCost = isBooking ? `$${Number(venue.booking.amount / 100).toLocaleString()}` : '';
  const bookingCost = isBooking
    ? venue?.booking?.totalPrice != null
      ? `$${(venue.booking.totalPrice / 100).toLocaleString()}`
      : venue?.booking?.amount != null
      ? `$${(venue.booking.amount / 100).toLocaleString()}`
      : ''
    : '';
  const payments = venue?.booking?.payments || [];

  const paymentsFormat = payments.map((payment) => {
    const labelDate = moment(payment.date).format('MMM D, YYYY');
    const amount = `$${(payment.amount / 100).toFixed(2).toLocaleString()}`;
    console.log('🚀 ~ paymentsFormat ~ amount:', amount);
    const formatDatePayment = `Paid ${amount} on ${labelDate}`;
    return formatDatePayment;
  });

  const bookingCostLabel = isBooking ? `${bookingCost} (${daysCount} day${daysCount > 1 ? 's' : ''})${venue.booking.cancelledOn ? ' - cancelled' : ''}` : '';

  let bookingDateLabel = '';
  let isCancelled = false;
  if (isBooking) {
    const dateLabel = moment(venue.booking.bookedOn).format('MMM D, YYYY');

    // Billing B3.0a: the old copies had status 'paid' on every booking (a
    // first payment was made). The record's status is the booking's step, so
    // the word is fixed here: the same text as before.
    if (venue.booking.bookedOn && dateLabel) {
      bookingDateLabel = `Paid ${bookingCost} on ${dateLabel}`;
    }
    if (venue.booking.cancelledOn) {
      // If event is cancelled, show feedback
      isCancelled = true;
      const cancelDate = moment(venue.booking.cancelledOn).format('MMM D, YYYY');
      bookingDateLabel = `Cancelled on ${cancelDate}`;
    }
  }
  // Booking view shows the booker's name & email. Never the host's name:
  // venue records are public (security S3.3b).
  const bookingName = venue.booking ? venue.booking.customerName : '';
  const bookingEmail = venue.booking ? venue.booking.customerEmail : '';
  // Billing B3.0a: the rest is due (record status `reserved`). Never on a cancelled booking.
  const isReserved = venue?.booking?.isReserved === true;
  const typeBooking = venue?.booking?.typeBooking;
  // Billing B4.3: '4:00 PM to 11:00 PM', or 'Starts 4:00 PM Nov 14. Ends 11:00 PM Nov 15.'
  // Empty for a booking with no times (made before B4.3).
  const from = isBooking ? timeLabel(venue.booking.startTime) : '';
  const to = isBooking ? timeLabel(venue.booking.endTime) : '';
  const timeText =
    from && to
      ? venue.booking.start === venue.booking.end
        ? `${from} to ${to}`
        : `Starts ${from} ${moment(venue.booking.start).format('MMM D')}. Ends ${to} ${moment(venue.booking.end).format('MMM D')}.`
      : '';

  return {
    isBooking,
    isCancelled,
    location,
    startLabel,
    endLabel,
    dateLabel,
    timeText,
    daysCount,
    bookingCost,
    bookingCostLabel,
    bookingDateLabel,
    bookingName,
    bookingEmail,
    paymentsFormat,
    isReserved,
    typeBooking
  };
};

// Billing B3.0a: takes the host's bookings (src/firebase/bookings.js).
export const getHostedBookingsByVenue = (bookings) =>
  (bookings || []).reduce(
    (all, booking) => ({
      ...all,
      [booking.venue_id]: [...(all[booking.venue_id] || []), booking]
    }),
    {}
  );

export const makeSearchUrl = ({
  location: loc,
  start,
  end,
  guests,
  page,
  id // venue id selected for detail-view
}) => {
  loc = loc || ANY_LOCATION;
  start = start && start !== 'none' ? moment(start).format('YYYY-MM-DD') : 'none';
  end = end && end !== 'none' ? moment(end).format('YYYY-MM-DD') : 'none';
  guests = Number(guests) || 0;
  page = Number(page) || 1;

  const query = new URLSearchParams({
    loc,
    start,
    end,
    guests,
    page,
    ...(id ? { id } : {})
  });
  return `/search/?${query.toString()}`;
};

export const makeFavoritesUrl = ({
  page,
  id // venue id selected for detail-view
}) => {
  const query = new URLSearchParams({
    page: Number(page) || 1,
    ...(id ? { id } : {})
  });
  return `/favorites/?${query.toString()}`;
};

export const makeReservationsUrl = ({
  page,
  id // venue id selected for detail-view
}) => {
  const query = new URLSearchParams({
    page: Number(page) || 1,
    ...(id ? { id } : {})
  });
  return `/reservations/?${query.toString()}`;
};

export const makeHostReservationsUrl = ({
  page,
  id // venue id selected for detail-view
}) => {
  const query = new URLSearchParams({
    page: Number(page) || 1,
    ...(id ? { id } : {})
  });
  return `/host/reservations/?${query.toString()}`;
};

export const makeHostedUrl = ({
  page,
  id // venue id selected for detail-view
}) => {
  const query = new URLSearchParams({
    page: Number(page) || 1,
    ...(id ? { id } : {})
  });
  return `/host/venues/?${query.toString()}`;
};

// NOTE: used by all list pages: search, favorites, reservations, ...
export const useSearchParams = () => {
  const { search } = useLocation();
  const query = new URLSearchParams(search);

  // make the names consistent with makeSearchUrl
  return {
    location: query.get('loc'),
    guests: Number(query.get('guests')),
    start: query.get('start') || 'none',
    end: query.get('end') || 'none',
    page: Math.max(Number(query.get('page')), 1),
    id: query.get('id')
  };
};

export const amenities = [
  'On-site Bathrooms',
  'Changing Rooms',
  'Dance Floor',
  'WiFi',
  'Tables',
  'Lights',
  'Kitchen',
  'Chairs',
  'Brides Room',
  'On-site Parking',
  'Pool',
  'Running Water',
  'Linens',
  'Staging Room'
];

export const states = [
  'AL',
  'AK',
  'AR',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WI',
  'WV',
  'WY'
];
